import { DrawerProps } from "@/../../../packages/a4b-ui/src/modules/admin/screens/SocialScreenContainer";
import { ReactNode, useMemo, useEffect } from "react";
import AntdFormWrapper from "../../../../../components/Forms/AntdFormWrapper";
import { FormInstance } from "antd";
import CreateAvatarForm from "../components/CreateAvatarForm";
import { Shrine } from "@a4b/api/src/modules/Temple/types";
import { useAppContext } from "../../../../../components/AppContext";
import { parseAvatarScheduleData } from "../../schedule/utils/helper";

export enum DRAWER_TYPE {
    CREATE_AVATAR = "Create Avatar",
    UPDATE_AVATAR = "Update Avatar",
    CLONE_AVATAR = "CLONE_AVATAR",
}

export default function useDrawerConfig(
    loadAvatars: () => Promise<void>,
    onDrawerClose: () => void,
    _setActiveDrawer: React.Dispatch<React.SetStateAction<DRAWER_TYPE | undefined>>,
    god_id: string,
    avatar: Shrine.AvatarPayload | undefined,
    activeDrawer?: DRAWER_TYPE
):
    | { drawerProps: DrawerProps; drawerContent: ReactNode }
    | { drawerProps: undefined; drawerContent: undefined } {
    const { networkInstance } = useAppContext();

    // Prepare values to prefill the form for clone (but DO NOT pass as `formData` to the wrapper)
    const prepareCloneValues = (a?: Shrine.AvatarPayload) => {
        if (!a) return undefined as any;
        const fd: any = parseAvatarScheduleData(a);
        // Ensure inactive in UI for clone
        fd.active = false;
        // Avoid leaking id into the form values
        if ("id" in fd) delete fd.id;
        // clear out image fields
        if ("attachment" in fd) fd.attachment = undefined;
        fd.code = "";
        return fd;
    };

    // FormFields wrapper for clone: prefill via setFieldsValue and keep wrapper in CREATE mode
    const CloneFormFields = ({ form }: { form: FormInstance }) => {
        const values = prepareCloneValues(avatar);
        useEffect(() => {
            if (values) {
                form.setFieldsValue(values);
                form.setFieldsValue({ attachment: undefined });
            }
        }, [form, avatar?.id]); // re-run if source avatar changes
        return <CreateAvatarForm form={form} mode="clone" />;
    };

    const Drawers: { drawerProps: DrawerProps; drawerContent: ReactNode }[] = useMemo(
        () => [
            // CREATE
            {
                drawerProps: {
                    visible: true,
                    title: DRAWER_TYPE.CREATE_AVATAR,
                    width: 1000,
                },
                drawerContent: (
                    <AntdFormWrapper<Shrine.AvatarPayload, Shrine.AvatarPayload>
                        FormFields={CreateAvatarForm}
                        createApi={async (payload) => {
                            if (payload?.promotional_avatar_details?.target_config) {
                                payload.promotional_avatar_details.target_config = JSON.parse(
                                    String(payload?.promotional_avatar_details?.target_config)
                                );
                            }
                            // clean null/empty fields
                            payload = cleanPayload(payload);

                            return networkInstance.clientWithHeaders.shrineApi.createAvatars(god_id, payload);
                        }}
                        submitSuccessfulCallback={() => {
                            onDrawerClose();
                            loadAvatars();
                        }}
                    />
                ),
            },
            // UPDATE
            {
                drawerProps: {
                    visible: true,
                    title: DRAWER_TYPE.UPDATE_AVATAR,
                    width: 900,
                },
                drawerContent: (
                    <AntdFormWrapper<Shrine.AvatarPayload, Shrine.AvatarPayload>
                        formData={parseAvatarScheduleData(avatar!)}
                        FormFields={(p: { form: FormInstance }) => <CreateAvatarForm {...p} mode="update" />}
                        updateApiId={async (avatarId, payload) => {
                            if (payload?.promotional_avatar_details?.target_config) {
                                payload.promotional_avatar_details.target_config = JSON.parse(
                                    String(payload?.promotional_avatar_details?.target_config)
                                );
                            }

                            // clean null/empty fields
                            payload = cleanPayload(payload);

                            return networkInstance.clientWithHeaders.shrineApi.updateAvatars(
                                god_id,
                                avatarId,
                                payload
                            );
                        }}

                        submitSuccessfulCallback={() => {
                            onDrawerClose();
                            loadAvatars();
                        }}
                    />
                ),
            },
            // CLONE
            {
                drawerProps: {
                    visible: true,
                    title: DRAWER_TYPE.CLONE_AVATAR,
                    width: 1000,
                },
                drawerContent: (
                    <AntdFormWrapper<Shrine.AvatarPayload, Shrine.AvatarPayload>
                        FormFields={CloneFormFields as unknown as (p: { form: FormInstance }) => JSX.Element}
                        createApi={async (payload) => {
                            //payload.active = false; 
                            (payload as any).clone_avatar_id = avatar?.id;

                            if (payload?.promotional_avatar_details?.target_config) {
                                payload.promotional_avatar_details.target_config = JSON.parse(
                                    String(payload?.promotional_avatar_details?.target_config)
                                );
                            }
                            // clean null/empty fields
                            payload = cleanPayload(payload);

                            return networkInstance.clientWithHeaders.shrineApi.createAvatars(god_id, payload);
                        }}
                        submitSuccessfulCallback={() => {
                            onDrawerClose();
                            loadAvatars();
                        }}
                    />
                ),
            },
        ],
        [avatar, god_id, loadAvatars, networkInstance.clientWithHeaders.shrineApi, onDrawerClose]
    );

    function cleanPayload(obj: any): any {
        if (Array.isArray(obj)) {
            return obj.map(cleanPayload).filter(v => v !== undefined);
        } else if (obj && typeof obj === "object") {
            const cleaned: any = {};
            Object.entries(obj).forEach(([key, value]) => {
                const val = cleanPayload(value);
                if (
                    val !== null &&
                    val !== undefined &&
                    !(typeof val === "object" && !Array.isArray(val) && Object.keys(val).length === 0)
                ) {
                    cleaned[key] = val;
                }
            });
            return cleaned;
        }
        return obj;
    }

    return (
        Drawers.find((Drawer) => Drawer.drawerProps.title === activeDrawer) || {
            drawerProps: undefined,
            drawerContent: undefined,
        }
    );
}
